import React, { useEffect, useState, useCallback } from "react";
import * as api from "../api";
import { useToast } from "../components/Toast";
import Modal, { ModalActions } from "../components/Modal";
import { PlusIcon, TrashIcon, EditIcon, XIcon } from "../components/Icons";
import DownloadMenu from "../components/DownloadMenu";

const COST_TABS = [
  { key: "development",    label: "Development Cost (Excl. Finance Cost)" },
  { key: "oh_contingency", label: "Project OH & Contingency" },
  { key: "total",          label: "Total Cost (w/o IDC)" },
];

const TOP_TABS = [
  { key: "capex",  label: "Capex Year-wise" },
  { key: "mof",    label: "Capex Means of Finance" },
  { key: "budget", label: "Budget Summary" },
];

const BUDGET_COST_FIELDS = [
  { key: "land",              label: "Land & Historical Cost" },
  { key: "development",       label: "Development Cost (incl. all)" },
  { key: "project_overhead",  label: "Project Overhead" },
  { key: "contingency",       label: "Contingency" },
  { key: "interest_cost",     label: "Interest Cost" },
];
const BUDGET_MOF_FIELDS = [
  { key: "equity",         label: "Equity / Internal Accrual" },
  { key: "unsecured_loan", label: "Unsecured Loan" },
  { key: "bank_loan",      label: "Bank Loan" },
];

const fmt = (n) => {
  if (n == null || isNaN(n) || Number(n) === 0) return "—";
  return Math.round(Number(n)).toLocaleString("en-IN");
};

export default function InputPage({ canEdit }) {
  const [activeTop, setActiveTop] = useState("capex");
  // Default to "Total Cost (w/o IDC)" — the read-only summary view —
  // so users land on the rollup, not one of the editable sub-tabs.
  const [activeCost, setActiveCost] = useState("total");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Inline editing for budget / incurred / year cells.
  // editing: { rowId, field ("budget" | "incurred_till" | year), draft }
  const [editing, setEditing] = useState(null);
  // Particulars text inputs are debounced — track local edits so the user can
  // type without each keystroke firing a PUT. Saved on blur or Enter.
  const [particularsDraft, setParticularsDraft] = useState({});  // { rowId: text }
  const [saving, setSaving] = useState(false);
  // ── Filter pane state (collapsible). All arrays = multi-select.
  // Empty arrays = no filter on that dimension. Applied to every sub-tab.
  const [filterCollapsed, setFilterCollapsed] = useState(true);
  const [filterProjectIds, setFilterProjectIds] = useState([]);
  const [filterCompanyIds, setFilterCompanyIds] = useState([]);
  const [filterVerticalIds, setFilterVerticalIds] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  // Catalog used by the filter pane (loaded once on mount).
  const [filterCatalog, setFilterCatalog] = useState({ projects: [], companies: [], verticals: [] });
  // Historical-year column controls. Default view = forward window (Actual+1
  // .. Actual+5). Users click "Show Historical" to expand or
  // "+ Add Historical Year" to pin a new prior-year column.
  const [showHistorical, setShowHistorical] = useState(false);
  const [addHistYear, setAddHistYear] = useState(null);
  const [savingHistYear, setSavingHistYear] = useState(false);
  const toast = useToast();

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return api.getCapex()
      .then(setData)
      .catch(() => toast("Failed to load capex data", "error"))
      .finally(() => { if (!silent) setLoading(false); });
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Filter-pane catalog: load projects + companies + verticals once.
  useEffect(() => {
    Promise.all([
      api.getProjects({ light: true }).catch(() => []),
      api.getCompanies().catch(() => []),
      api.getVerticals().catch(() => []),
    ]).then(([p, c, v]) => {
      setFilterCatalog({
        projects: Array.isArray(p) ? p : [],
        companies: Array.isArray(c) ? c : [],
        verticals: Array.isArray(v) ? v : [],
      });
    });
  }, []);

  // Compute the set of visible project IDs given the active filters. Each
  // filter dimension narrows via AND; empty filter = no restriction.
  const visibleProjectIds = React.useMemo(() => {
    const proj = filterCatalog.projects;
    const matched = proj.filter((p) => {
      if (filterProjectIds.length && !filterProjectIds.includes(p.id)) return false;
      if (filterCompanyIds.length && !filterCompanyIds.includes(p.company_id)) return false;
      if (filterVerticalIds.length && !filterVerticalIds.includes(p.vertical_id)) return false;
      if (filterStatuses.length && !filterStatuses.includes(p.status)) return false;
      return true;
    });
    // null = no filtering applied (every project allowed)
    const hasAny = filterProjectIds.length || filterCompanyIds.length
      || filterVerticalIds.length || filterStatuses.length;
    return hasAny ? new Set(matched.map((p) => p.id)) : null;
  }, [filterCatalog.projects, filterProjectIds, filterCompanyIds, filterVerticalIds, filterStatuses]);

  const resetFilters = () => {
    setFilterProjectIds([]);
    setFilterCompanyIds([]);
    setFilterVerticalIds([]);
    setFilterStatuses([]);
  };
  const activeFilterCount = filterProjectIds.length + filterCompanyIds.length
    + filterVerticalIds.length + filterStatuses.length;

  const isComputed = activeCost === "total";

  const beginEdit = (rowId, field, current) => {
    if (!canEdit || isComputed) return;
    setEditing({ rowId, field, draft: current == null || current === 0 ? "" : String(current) });
  };

  const commitEdit = async () => {
    if (!editing) return;
    const { rowId, field, draft } = editing;
    const value = parseFloat(draft) || 0;
    setSaving(true);
    try {
      const body = { value };
      if (field === "budget" || field === "incurred_till") {
        body.field = field;
      } else {
        body.field = "year";
        body.year = field;
      }
      await api.updateCapexRow(rowId, body);
      setEditing(null);
      load(true);
    } catch (e) {
      toast(e.message, "error");
    }
    setSaving(false);
  };

  const cancelEdit = () => setEditing(null);

  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
    if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
  };

  const handleAddRow = async () => {
    if (!data || (data.projects || []).length === 0) {
      toast("Add a project first under the Projects tab.", "error");
      return;
    }
    try {
      await api.addCapexRow({
        project_id: data.projects[0].id,
        cost_type: activeCost,
        particulars: "",
      });
      load(true);
    } catch (e) { toast(e.message, "error"); }
  };

  const handleDeleteRow = async (rowId) => {
    try {
      await api.deleteCapexRow(rowId);
      load(true);
    } catch (e) { toast(e.message, "error"); }
  };

  const handleProjectChange = async (rowId, newPid) => {
    try {
      await api.updateCapexRow(rowId, { field: "project_id", value: newPid });
      load(true);
    } catch (e) { toast(e.message, "error"); }
  };

  const handleParticularsCommit = async (rowId, currentValue) => {
    const draft = particularsDraft[rowId];
    if (draft == null || draft === currentValue) return;
    try {
      await api.updateCapexRow(rowId, { field: "particulars", value: draft });
      setParticularsDraft((p) => { const n = { ...p }; delete n[rowId]; return n; });
      load(true);
    } catch (e) { toast(e.message, "error"); }
  };

  const openAddHistoricalYear = () => {
    if (!data) return;
    const existing = new Set((data.historical_years || []).map((y) => parseInt(y, 10)));
    let candidate = (data.actual_year || 2025) - 1;
    while (existing.has(candidate) && candidate > 1900) candidate -= 1;
    setAddHistYear(candidate);
  };
  const submitAddHistoricalYear = async () => {
    if (!data || addHistYear == null) return;
    const yr = parseInt(addHistYear, 10);
    if (isNaN(yr)) { toast("Enter a valid year", "error"); return; }
    if (yr >= (data.actual_year || 2025)) {
      toast(`Year must be earlier than FY ${data.actual_year}`, "error");
      return;
    }
    setSavingHistYear(true);
    try {
      await api.addCapexHistoricalYear(yr);
      toast(`Historical year FY ${yr} added`);
      setAddHistYear(null);
      setShowHistorical(true);
      await load(true);
    } catch (e) { toast(e.message, "error"); }
    setSavingHistYear(false);
  };
  const removeHistoricalYear = async (yr) => {
    if (!window.confirm(`Remove historical column FY ${yr}? All capex values entered for this year will be cleared.`)) return;
    try {
      await api.removeCapexHistoricalYear(parseInt(yr, 10));
      toast(`Historical year FY ${yr} removed`);
      await load(true);
    } catch (e) { toast(e.message, "error"); }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!data) return <p className="text-muted">No data</p>;

  const { actual_year, projects, entries } = data;
  // `data.years` from the backend is historical + forward; filter out the
  // historical columns by default so the on-screen view matches the
  // canonical Actual + Budgeted + 4 Projected window. The Show Historical
  // toggle re-introduces them.
  const allYears = data.years || [];
  const historicalYears = (data.historical_years || []).map((y) => parseInt(y, 10));
  const isHistoricalYear = (y) => parseInt(y, 10) < actual_year;
  const years = showHistorical
    ? allYears
    : allYears.filter((y) => !isHistoricalYear(y));
  const allRows = entries[activeCost] || [];
  // Apply the filter pane's visibleProjectIds (null = no filter applied).
  const rows = visibleProjectIds
    ? allRows.filter((r) => visibleProjectIds.has(r.project_id))
    : allRows;

  // Aggregate column totals across all rows.
  const totals = {
    budget: 0,
    incurred_till: 0,
    balance: 0,
    years: years.reduce((acc, y) => ({ ...acc, [y]: 0 }), {}),
  };
  rows.forEach((r) => {
    totals.budget += Number(r.budget || 0);
    totals.incurred_till += Number(r.incurred_till || 0);
    totals.balance += Number(r.balance || 0);
    years.forEach((y) => {
      totals.years[y] += Number((r.year_values || {})[String(y)] || 0);
    });
  });

  const projectName = (pid) =>
    projects.find((p) => p.id === pid)?.name || pid;

  const renderEditableCell = (rowId, field, value) => {
    const isEditing = editing && editing.rowId === rowId && editing.field === field;
    if (isEditing) {
      return (
        <input autoFocus type="number"
          className="input"
          style={{ width: "100%", padding: "4px 6px", fontSize: 12, textAlign: "right" }}
          value={editing.draft}
          onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
          onBlur={commitEdit}
          onKeyDown={onKey}
          disabled={saving} />
      );
    }
    const editable = canEdit && !isComputed;
    return (
      <span
        onClick={() => beginEdit(rowId, field, value)}
        style={{ cursor: editable ? "pointer" : "default", display: "block" }}
        title={editable ? "Click to edit" : (isComputed ? "Auto-computed: Development + OH & Contingency" : undefined)}
      >
        {fmt(value)}
      </span>
    );
  };

  return (
    <div className="fade-in">
      {/* ── Collapsible filter pane ──
          Hidden by default; applies to every Input sub-tab. */}
      <div className="card mb-12" style={{ padding: 0 }}>
        <button type="button"
          onClick={() => setFilterCollapsed((p) => !p)}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: 8,
            background: "transparent", border: "none", cursor: "pointer",
            padding: "8px 14px", color: "inherit", textAlign: "left",
          }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
            style={{ transform: filterCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", color: "var(--text-secondary)" }}>
            <path d="M6 9l6 6 6-6" />
          </svg>
          <span className="fw-600" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-secondary)" }}>
            Filter
          </span>
          {activeFilterCount > 0 && (
            <span className="text-xxs fw-600" style={{
              padding: "1px 8px", borderRadius: 10,
              background: "rgba(124,143,245,0.18)", color: "var(--accent)",
              border: "1px solid rgba(124,143,245,0.40)", marginLeft: 4,
            }}>{activeFilterCount} active</span>
          )}
          <span className="text-xxs text-dim" style={{ marginLeft: "auto" }}>
            {visibleProjectIds ? `${visibleProjectIds.size} of ${filterCatalog.projects.length} projects` : `${filterCatalog.projects.length} projects (all)`}
          </span>
        </button>
        {!filterCollapsed && (
          <div style={{ padding: "0 14px 12px" }}>
            <div className="flex items-center" style={{ gap: 8, flexWrap: "wrap" }}>
              <FilterMultiSelect
                placeholder="— Any vertical —"
                options={filterCatalog.verticals.map((v) => ({ id: v.id, label: v.name }))}
                value={filterVerticalIds} onChange={setFilterVerticalIds} />
              <FilterMultiSelect
                placeholder="— Any company —"
                options={filterCatalog.companies.map((c) => ({
                  id: c.id, label: `${c.name}${c.code ? ` (${c.code})` : ""}`,
                }))}
                value={filterCompanyIds} onChange={setFilterCompanyIds} />
              <FilterMultiSelect
                placeholder="— Any project —"
                options={filterCatalog.projects.map((p) => ({ id: p.id, label: p.name }))}
                value={filterProjectIds} onChange={setFilterProjectIds} />
              <FilterMultiSelect
                placeholder="— Any status —"
                options={[
                  { id: "Ongoing", label: "Under Construction" },
                  { id: "Completed", label: "Operational" },
                ]}
                value={filterStatuses} onChange={setFilterStatuses} />
              {activeFilterCount > 0 && (
                <button className="btn btn-ghost btn-sm" onClick={resetFilters}>Reset</button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="tabs mb-20">
        {TOP_TABS.map((t) => (
          <button key={t.key}
            className={`tab ${activeTop === t.key ? "active" : ""}`}
            onClick={() => setActiveTop(t.key)}>{t.label}</button>
        ))}
      </div>

      {activeTop === "capex" && (
        <div className="card">
          <div className="flex justify-between items-center mb-16" style={{ flexWrap: "wrap", gap: 12 }}>
            <div>
              <h3 className="heading fw-600" style={{ fontSize: 16 }}>
                {COST_TABS.find((c) => c.key === activeCost)?.label}
              </h3>
              <p className="text-xs text-muted mt-4">
                {isComputed
                  ? "Auto-computed per project: Development Cost + Project OH & Contingency. Edit those tabs to update these values."
                  : <>Year-wise capex line items.{canEdit && " Pick a Project, name the Particular, click any value cell to edit."}</>}
              </p>
            </div>
            <div className="flex items-center gap-12" style={{ flexWrap: "wrap" }}>
              <div className="tabs" style={{ padding: 3 }}>
                {COST_TABS.map((c) => (
                  <button key={c.key} type="button"
                    className={`tab ${activeCost === c.key ? "active" : ""}`}
                    onClick={() => { setActiveCost(c.key); setEditing(null); }}>
                    {c.label}
                  </button>
                ))}
              </div>
              <DownloadMenu
                filename={`capex_${activeCost}`}
                getData={() => ({
                  title: `Capex — ${COST_TABS.find((c) => c.key === activeCost)?.label || activeCost}`,
                  columns: [
                    { key: "sl", label: "Sl No" },
                    { key: "project", label: "Project" },
                    { key: "particulars", label: "Particulars" },
                    { key: "budget", label: "Budget" },
                    { key: "incurred", label: `Incurred Till FY ${String(actual_year).slice(-2)}` },
                    { key: "balance", label: "Balance to be Incurred" },
                    ...years.map((y) => ({ key: `y${y}`, label: `FY ${y}` })),
                  ],
                  rows: rows.map((r, i) => ({
                    sl: i + 1,
                    project: projectName(r.project_id),
                    particulars: r.particulars || "",
                    budget: r.budget == null ? "" : Number(r.budget).toFixed(2),
                    incurred: r.incurred_till == null ? "" : Number(r.incurred_till).toFixed(2),
                    balance: r.balance == null ? "" : Number(r.balance).toFixed(2),
                    ...Object.fromEntries(years.map((y) => [
                      `y${y}`,
                      (r.year_values || {})[String(y)] == null
                        ? ""
                        : Number((r.year_values || {})[String(y)]).toFixed(2),
                    ])),
                    // For the Total Cost summary view, the Formula column
                    // shows the derivation rule.
                    _formula_text: isComputed
                      ? "Total Cost = Development + OH & Contingency"
                      : "",
                  })),
                })} />
              {historicalYears.length > 0 && (
                <button className={`btn btn-sm ${showHistorical ? "btn-primary" : "btn-ghost"}`}
                  onClick={() => setShowHistorical((p) => !p)}
                  title={showHistorical ? "Hide historical year columns" : "Show historical year columns"}>
                  {showHistorical ? "Hide Historical" : `Show Historical (${historicalYears.length})`}
                </button>
              )}
              {canEdit && (
                <button className="btn btn-ghost btn-sm" onClick={openAddHistoricalYear}
                  title={`Pin a historical year column (year < FY ${actual_year}) for manual capex entry.`}>
                  <PlusIcon /> Add Historical Year
                </button>
              )}
              {canEdit && !isComputed && (
                <button className="btn btn-primary btn-sm" onClick={handleAddRow}>
                  <PlusIcon /> Add Row
                </button>
              )}
            </div>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>Sl No</th>
                  <th style={{ minWidth: 180, width: 200 }}>Project</th>
                  <th style={{ minWidth: 180 }}>Particulars</th>
                  <th style={{ textAlign: "right", width: 90 }}>Budget</th>
                  <th style={{ textAlign: "right", width: 110 }}>
                    <div className="text-xxs text-dim">Cumulative</div>
                    Incurred Till FY {String(actual_year).slice(-2)}
                  </th>
                  <th style={{ textAlign: "right", width: 110 }}>
                    <div className="text-xxs text-dim">Computed</div>
                    Balance to be Incurred
                  </th>
                  {years.map((y) => {
                    const isHist = isHistoricalYear(y);
                    return (
                      <th key={y} style={{
                        textAlign: "right", width: 90,
                        background: isHist ? "rgba(148,163,184,0.06)" : undefined,
                      }}>
                        {isHist && (
                          <div className="text-xxs" style={{ color: "var(--text-dim)", fontStyle: "italic" }}>
                            Historical
                          </div>
                        )}
                        FY {y}
                      </th>
                    );
                  })}
                  {canEdit && !isComputed && <th style={{ width: 40 }} />}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={6 + years.length + (canEdit && !isComputed ? 1 : 0)}
                          className="text-muted"
                          style={{ textAlign: "center", padding: 24 }}>
                    {isComputed
                      ? "No projects with capex line items yet. Add rows under Development or OH & Contingency."
                      : <>No capex line items yet.{canEdit && " Click \"Add Row\" to start."}</>}
                  </td></tr>
                )}
                {rows.map((r, idx) => {
                  const draft = particularsDraft[r.id];
                  const particularsValue = draft != null ? draft : (r.particulars || "");
                  return (
                    <tr key={r.id}>
                      <td className="mono text-xs text-dim">{idx + 1}</td>
                      <td>
                        {isComputed ? (
                          <span className="fw-500 text-sm">{projectName(r.project_id)}</span>
                        ) : canEdit ? (
                          <select className="input select" style={{ width: "100%", padding: "4px 8px", fontSize: 12 }}
                            value={r.project_id}
                            onChange={(e) => handleProjectChange(r.id, e.target.value)}>
                            {projects.map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="fw-500 text-sm">{projectName(r.project_id)}</span>
                        )}
                      </td>
                      <td>
                        {isComputed ? (
                          <span className="text-sm">{r.particulars || <span className="text-dim">—</span>}</span>
                        ) : canEdit ? (
                          <input className="input" style={{ width: "100%", padding: "4px 8px", fontSize: 12 }}
                            value={particularsValue}
                            placeholder="e.g. Land, Building, Equipment"
                            onChange={(e) => setParticularsDraft((p) => ({ ...p, [r.id]: e.target.value }))}
                            onBlur={() => handleParticularsCommit(r.id, r.particulars || "")}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); e.target.blur(); }
                              if (e.key === "Escape") {
                                setParticularsDraft((p) => { const n = { ...p }; delete n[r.id]; return n; });
                                e.target.blur();
                              }
                            }} />
                        ) : (
                          <span className="text-sm">{r.particulars || "—"}</span>
                        )}
                      </td>
                      <td className="mono text-xs" style={{ textAlign: "right" }}>
                        {renderEditableCell(r.id, "budget", r.budget)}
                      </td>
                      <td className="mono text-xs" style={{ textAlign: "right" }}>
                        {renderEditableCell(r.id, "incurred_till", r.incurred_till)}
                      </td>
                      <td className="mono text-xs" style={{ textAlign: "right",
                        color: "var(--text-secondary)", background: "rgba(255,255,255,0.02)" }}>
                        {fmt(r.balance)}
                      </td>
                      {years.map((y) => (
                        <td key={y} className="mono text-xs" style={{ textAlign: "right",
                          background: isHistoricalYear(y) ? "rgba(148,163,184,0.05)" : undefined }}>
                          {renderEditableCell(r.id, y, (r.year_values || {})[String(y)])}
                        </td>
                      ))}
                      {canEdit && !isComputed && (
                        <td>
                          <button className="btn-icon" title="Delete row"
                            onClick={() => handleDeleteRow(r.id)}>
                            <TrashIcon />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {rows.length > 0 && (
                  <tr style={{
                    background: "rgba(124,143,245,0.14)",
                    fontWeight: 700,
                    borderTop: "2px solid rgba(124,143,245,0.40)",
                  }}>
                    <td />
                    <td className="fw-700" style={{ color: "var(--accent)" }}>Total</td>
                    <td />
                    <td className="mono text-xs fw-700" style={{ textAlign: "right" }}>{fmt(totals.budget)}</td>
                    <td className="mono text-xs fw-700" style={{ textAlign: "right" }}>{fmt(totals.incurred_till)}</td>
                    <td className="mono text-xs fw-700" style={{ textAlign: "right" }}>{fmt(totals.balance)}</td>
                    {years.map((y) => (
                      <td key={y} className="mono text-xs fw-700" style={{ textAlign: "right",
                        background: isHistoricalYear(y) ? "rgba(148,163,184,0.10)" : undefined }}>
                        {fmt(totals.years[y])}
                      </td>
                    ))}
                    {canEdit && !isComputed && <td />}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTop === "mof" && <MeansOfFinancePane canEdit={canEdit} />}
      {activeTop === "budget" && <BudgetSummaryPane canEdit={canEdit} />}

      {/* ── Add Historical Year Modal ──
          Pins a historical column (year < Actual FY) to the Capex Year-wise
          table. Applies globally — every project's capex line items get
          editable cells for that year. */}
      {addHistYear != null && (
        <Modal title="Add Historical Year" onClose={() => !savingHistYear && setAddHistYear(null)}>
          <p className="text-xs text-muted mb-12">
            Adds an editable Capex column for the chosen fiscal year across
            every project. Cells start blank — type values directly to record
            historical capex outflows.
          </p>
          <div className="form-group">
            <label className="form-label">Fiscal Year</label>
            <input className="input" type="number" autoFocus
              value={addHistYear} min={1900} max={(actual_year || 2025) - 1}
              onChange={(e) => setAddHistYear(e.target.value === "" ? "" : parseInt(e.target.value, 10))}
              onKeyDown={(e) => { if (e.key === "Enter" && !savingHistYear) submitAddHistoricalYear(); }} />
            <p className="text-xxs text-dim mt-4">
              Must be earlier than the default Actual FY ({actual_year}).
              {historicalYears.length > 0 && (
                <> Already added: {historicalYears.map((y) => `FY ${y}`).join(", ")}.</>
              )}
            </p>
          </div>
          <ModalActions onCancel={() => setAddHistYear(null)}
            onConfirm={submitAddHistoricalYear}
            confirmLabel={savingHistYear ? "Adding…" : "Add Year"}
            disabled={savingHistYear || addHistYear === "" || addHistYear == null
                      || parseInt(addHistYear, 10) >= (actual_year || 2025)} />
          {historicalYears.length > 0 && (
            <div style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
              <p className="text-xxs text-muted fw-600" style={{ textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
                Existing Historical Columns
              </p>
              <div className="flex" style={{ gap: 6, flexWrap: "wrap" }}>
                {historicalYears.map((y) => (
                  <span key={y} className="flex items-center"
                    style={{
                      padding: "3px 8px", borderRadius: 6,
                      background: "rgba(148,163,184,0.10)",
                      border: "1px solid var(--border)",
                      gap: 6, fontSize: 12,
                    }}>
                    FY {y}
                    {canEdit && (
                      <button type="button" className="btn-icon"
                        title={`Remove FY ${y} column`}
                        onClick={() => removeHistoricalYear(y)}
                        style={{ padding: 2 }}>
                        <XIcon />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}


/* ─── Capex Means of Finance ─── */
function MeansOfFinancePane({ canEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // { rowId, field, draft }
  const [particularsDraft, setParticularsDraft] = useState({});
  const toast = useToast();

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return api.getMeansOfFinance()
      .then(setData)
      .catch(() => toast("Failed to load means of finance", "error"))
      .finally(() => { if (!silent) setLoading(false); });
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleAddRow = async () => {
    if (!data || (data.projects || []).length === 0) {
      toast("Add a project first under the Projects tab.", "error");
      return;
    }
    try {
      await api.addMeansOfFinanceRow({ project_id: data.projects[0].id });
      load(true);
    } catch (e) { toast(e.message, "error"); }
  };

  const handleDelete = async (rid) => {
    try { await api.deleteMeansOfFinanceRow(rid); load(true); }
    catch (e) { toast(e.message, "error"); }
  };

  const handleField = async (rid, field, value) => {
    try {
      await api.updateMeansOfFinanceRow(rid, { field, value });
      load(true);
    } catch (e) { toast(e.message, "error"); }
  };

  const beginEdit = (rowId, field, current) => {
    if (!canEdit) return;
    setEditing({ rowId, field, draft: current == null || current === 0 ? "" : String(current) });
  };
  const commitEdit = async () => {
    if (!editing) return;
    const { rowId, field, draft } = editing;
    setEditing(null);
    await handleField(rowId, field, parseFloat(draft) || 0);
  };
  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
    if (e.key === "Escape") { e.preventDefault(); setEditing(null); }
  };

  const renderEditable = (rowId, field, value) => {
    const isEditing = editing && editing.rowId === rowId && editing.field === field;
    if (isEditing) {
      return (
        <input autoFocus type="number"
          className="input"
          style={{ width: "100%", padding: "4px 6px", fontSize: 12, textAlign: "right" }}
          value={editing.draft}
          onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
          onBlur={commitEdit}
          onKeyDown={onKey} />
      );
    }
    return (
      <span onClick={() => beginEdit(rowId, field, value)}
        style={{ cursor: canEdit ? "pointer" : "default", display: "block" }}
        title={canEdit ? "Click to edit" : undefined}>
        {fmt(value)}
      </span>
    );
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!data) return <p className="text-muted">No data</p>;

  const { rows, projects, verticals } = data;

  // Aggregated totals (sum across all rows).
  const totals = rows.reduce((acc, r) => ({
    capex: acc.capex + Number(r.capex || 0),
    equity: acc.equity + Number(r.equity || 0),
    unsecured_loan: acc.unsecured_loan + Number(r.unsecured_loan || 0),
    bank_loan: acc.bank_loan + Number(r.bank_loan || 0),
    total: acc.total + Number(r.total || 0),
  }), { capex: 0, equity: 0, unsecured_loan: 0, bank_loan: 0, total: 0 });

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-16" style={{ flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 className="heading fw-600" style={{ fontSize: 16 }}>Capex Means of Finance</h3>
          <p className="text-xs text-muted mt-4">
            Equity (Net Worth) = Capex − Unsecured Loan − Bank Loan. Total = Equity + Unsecured Loan + Bank Loan (≡ Capex).
            {canEdit && " Pick a Project (Vertical auto-fills); click any value cell to edit."}
          </p>
        </div>
        {canEdit && (
          <button className="btn btn-primary btn-sm" onClick={handleAddRow}>
            <PlusIcon /> Add Row
          </button>
        )}
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th rowSpan={2} style={{ width: 50, textAlign: "center", verticalAlign: "middle" }}>Sl No</th>
              <th rowSpan={2} style={{ minWidth: 140, width: 160, textAlign: "center", verticalAlign: "middle" }}>Vertical</th>
              <th rowSpan={2} style={{ minWidth: 160, width: 180, textAlign: "center", verticalAlign: "middle" }}>Project</th>
              <th rowSpan={2} style={{ minWidth: 160, textAlign: "center", verticalAlign: "middle" }}>Particulars</th>
              <th rowSpan={2} style={{ textAlign: "center", width: 100, verticalAlign: "middle" }}>Capex</th>
              <th colSpan={2}
                style={{
                  textAlign: "center",
                  borderBottom: "1px solid rgba(124,143,245,0.35)",
                  color: "var(--accent)",
                  letterSpacing: 0.6, textTransform: "uppercase",
                  fontWeight: 600, paddingBottom: 6,
                  verticalAlign: "middle",
                }}>
                Promoters' Contribution
              </th>
              <th rowSpan={2} style={{ textAlign: "center", width: 110, verticalAlign: "middle" }}>Bank Loan</th>
              <th rowSpan={2} style={{ textAlign: "center", width: 110, verticalAlign: "middle" }}>
                <span className="flex items-center justify-center gap-4">
                  Total
                  <span title="Auto-computed: Equity + Unsecured Loan + Bank Loan"
                    style={{ display: "inline-flex", alignItems: "center", color: "var(--accent)" }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
                    </svg>
                  </span>
                </span>
              </th>
              {canEdit && <th rowSpan={2} style={{ width: 40 }} />}
            </tr>
            <tr>
              <th style={{ textAlign: "center", width: 140, verticalAlign: "middle" }}>
                <span className="flex items-center justify-center gap-4">
                  Equity / Net Worth
                  <span title="Auto-computed: Capex − Unsecured Loan − Bank Loan"
                    style={{ display: "inline-flex", alignItems: "center", color: "var(--accent)" }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
                    </svg>
                  </span>
                </span>
              </th>
              <th style={{ textAlign: "center", width: 120, verticalAlign: "middle" }}>Unsecured Loan</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9 + (canEdit ? 1 : 0)} className="text-muted"
                      style={{ textAlign: "center", padding: 24 }}>
                {canEdit
                  ? <>No rows yet. Click <strong>Add Row</strong> to start.</>
                  : "No rows yet."}
              </td></tr>
            )}
            {rows.map((r, idx) => {
              const draft = particularsDraft[r.id];
              const particularsValue = draft != null ? draft : (r.particulars || "");
              return (
                <tr key={r.id}>
                  <td className="mono text-xs text-dim">{idx + 1}</td>
                  <td>
                    {/* Vertical is now read-only — auto-derived from the
                        row's project. Edit the project's vertical via the
                        Company that owns it (Settings → Project ↔ Company). */}
                    <span className="text-sm"
                      title="Auto-fetched from the row's project. Change the project's vertical by editing the parent Company.">
                      {(() => {
                        const proj = projects.find((p) => p.id === r.project_id);
                        const vid = proj?.vertical_id || r.vertical_id;
                        return verticals.find((v) => v.id === vid)?.name || "—";
                      })()}
                    </span>
                  </td>
                  <td>
                    {canEdit ? (
                      <select className="input select" style={{ width: "100%", padding: "4px 8px", fontSize: 12 }}
                        value={r.project_id}
                        onChange={(e) => {
                          // Auto-sync vertical_id from the picked project so
                          // the persisted MoF row's vertical stays in step.
                          handleField(r.id, "project_id", e.target.value);
                          const proj = projects.find((p) => p.id === e.target.value);
                          if (proj && proj.vertical_id && proj.vertical_id !== r.vertical_id) {
                            handleField(r.id, "vertical_id", proj.vertical_id);
                          }
                        }}>
                        {projects.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="fw-500 text-sm">
                        {projects.find((p) => p.id === r.project_id)?.name || r.project_id}
                      </span>
                    )}
                  </td>
                  <td>
                    {canEdit ? (
                      <input className="input" style={{ width: "100%", padding: "4px 8px", fontSize: 12 }}
                        value={particularsValue}
                        placeholder="e.g. Land, Building, Equipment"
                        onChange={(e) => setParticularsDraft((p) => ({ ...p, [r.id]: e.target.value }))}
                        onBlur={async () => {
                          if (draft != null && draft !== (r.particulars || "")) {
                            await handleField(r.id, "particulars", draft);
                          }
                          setParticularsDraft((p) => { const n = { ...p }; delete n[r.id]; return n; });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); e.target.blur(); }
                          if (e.key === "Escape") {
                            setParticularsDraft((p) => { const n = { ...p }; delete n[r.id]; return n; });
                            e.target.blur();
                          }
                        }} />
                    ) : (
                      <span className="text-sm">{r.particulars || "—"}</span>
                    )}
                  </td>
                  <td className="mono text-xs" style={{ textAlign: "right" }}>
                    {renderEditable(r.id, "capex", r.capex)}
                  </td>
                  <td className="mono text-xs" style={{ textAlign: "right",
                    color: "var(--text-secondary)", background: "rgba(255,255,255,0.02)" }}>
                    {fmt(r.equity)}
                  </td>
                  <td className="mono text-xs" style={{ textAlign: "right" }}>
                    {renderEditable(r.id, "unsecured_loan", r.unsecured_loan)}
                  </td>
                  <td className="mono text-xs" style={{ textAlign: "right" }}>
                    {renderEditable(r.id, "bank_loan", r.bank_loan)}
                  </td>
                  <td className="mono text-xs" style={{ textAlign: "right",
                    color: "var(--text-secondary)", background: "rgba(255,255,255,0.02)" }}>
                    {fmt(r.total)}
                  </td>
                  {canEdit && (
                    <td>
                      <button className="btn-icon" title="Delete row" onClick={() => handleDelete(r.id)}>
                        <TrashIcon />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {rows.length > 0 && (
              <tr style={{
                background: "rgba(124,143,245,0.14)",
                fontWeight: 700,
                borderTop: "2px solid rgba(124,143,245,0.40)",
              }}>
                <td />
                <td className="fw-700" style={{ color: "var(--accent)" }}>Total</td>
                <td colSpan={2} />
                <td className="mono text-xs fw-700" style={{ textAlign: "right" }}>{fmt(totals.capex)}</td>
                <td className="mono text-xs fw-700" style={{ textAlign: "right" }}>{fmt(totals.equity)}</td>
                <td className="mono text-xs fw-700" style={{ textAlign: "right" }}>{fmt(totals.unsecured_loan)}</td>
                <td className="mono text-xs fw-700" style={{ textAlign: "right" }}>{fmt(totals.bank_loan)}</td>
                <td className="mono text-xs fw-700" style={{ textAlign: "right" }}>{fmt(totals.total)}</td>
                {canEdit && <td />}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ─── Budget Summary ─── */
function BudgetSummaryPane({ canEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // editing: { rowKey, field, draft }   rowKey = `${projectId}::${particulars}`
  const [editing, setEditing] = useState(null);
  // particulars text drafts keyed by rowKey
  const [particularsDraft, setParticularsDraft] = useState({});
  // formulaModal: { projectId, projectName, particulars, field, label, value, formula }
  const [formulaModal, setFormulaModal] = useState(null);
  const toast = useToast();

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return api.getBudgetSummary()
      .then(setData)
      .catch(() => toast("Failed to load budget summary", "error"))
      .finally(() => { if (!silent) setLoading(false); });
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const rowKey = (projectId, particulars) => `${projectId}::${particulars || ""}`;

  // ── Cell editing ──
  const beginEdit = (projectId, particulars, field, current) => {
    if (!canEdit) return;
    setEditing({
      rowKey: rowKey(projectId, particulars),
      field, draft: current == null ? "" : String(current),
      projectId, particulars,
    });
  };
  const commitEdit = async () => {
    if (!editing) return;
    const { projectId, particulars, field, draft } = editing;
    setEditing(null);
    try {
      const value = draft === "" ? null : parseFloat(draft);
      if (draft !== "" && isNaN(value)) return;
      await api.updateBudgetSummaryCell({
        project_id: projectId, particulars, field, value,
      });
      load(true);
    } catch (e) { toast(e.message, "error"); }
  };
  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
    if (e.key === "Escape") { e.preventDefault(); setEditing(null); }
  };

  // ── Row CRUD ──
  const handleAddRow = async (projectId) => {
    if (!projectId) return;
    try {
      // Find a unique default name to avoid uniqueness clash on the server.
      const existing = (data?.rows || [])
        .filter((r) => r.project_id === projectId)
        .map((r) => r.particulars || "");
      let name = "";
      if (existing.includes("")) {
        let n = 1;
        while (existing.includes(`Row ${n}`)) n += 1;
        name = `Row ${n}`;
      }
      await api.addBudgetSummaryRow({ project_id: projectId, particulars: name });
      load(true);
    } catch (e) { toast(e.message, "error"); }
  };
  const handleDeleteRow = async (projectId, particulars) => {
    if (!window.confirm(`Delete this Budget Summary row?`)) return;
    try {
      await api.deleteBudgetSummaryRow({ project_id: projectId, particulars });
      load(true);
    } catch (e) { toast(e.message, "error"); }
  };
  const handleRenameRow = async (projectId, oldP, newP) => {
    const oldTrim = (oldP || "").trim();
    const newTrim = (newP || "").trim();
    if (oldTrim === newTrim) return;
    try {
      await api.renameBudgetSummaryRow({
        project_id: projectId, old: oldTrim, new: newTrim,
      });
      load(true);
    } catch (e) { toast(e.message, "error"); load(true); }
  };

  // ── Formula modal ──
  const openFormulaModal = (projectId, projectName, particulars, field, label, cell) => {
    setFormulaModal({
      projectId, projectName, particulars, field, label,
      value: cell?.value ?? "",
      formula: cell?.formula || "",
    });
  };
  const saveFormula = async () => {
    if (!formulaModal) return;
    try {
      await api.updateBudgetSummaryCell({
        project_id: formulaModal.projectId,
        particulars: formulaModal.particulars,
        field: formulaModal.field,
        formula: formulaModal.formula || "",
      });
      setFormulaModal(null);
      load(true);
    } catch (e) { toast(e.message, "error"); }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!data) return <p className="text-muted">No data</p>;

  const { projects, rows } = data;
  const projectName = (pid) => projects.find((p) => p.id === pid)?.name || pid;

  // Display value — formula-computed wins over manual value when set.
  const cellValue = (cell) => {
    if (!cell) return null;
    if (cell.formula && cell.computed_value != null) return cell.computed_value;
    return cell.value;
  };

  const renderCell = (row, fieldDef) => {
    const cell = row.cells[fieldDef.key] || {};
    const display = cellValue(cell);
    const isEditing = editing
      && editing.rowKey === rowKey(row.project_id, row.particulars)
      && editing.field === fieldDef.key;
    const hasFormula = !!cell.formula;

    if (isEditing) {
      return (
        <input autoFocus type="number"
          className="input"
          style={{ width: "100%", padding: "4px 6px", fontSize: 12, textAlign: "right" }}
          value={editing.draft}
          onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
          onBlur={commitEdit}
          onKeyDown={onKey} />
      );
    }
    return (
      <div className="flex items-center justify-end gap-4">
        {hasFormula && (
          <span title={`Formula: ${cell.formula}`}
            style={{ display: "inline-flex", alignItems: "center", color: "var(--accent)" }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
            </svg>
          </span>
        )}
        <span
          onClick={() => !hasFormula && beginEdit(row.project_id, row.particulars, fieldDef.key, cell.value)}
          style={{
            cursor: canEdit && !hasFormula ? "pointer" : "default",
            opacity: hasFormula ? 0.85 : 1,
            flex: 1, textAlign: "right",
          }}
          title={hasFormula ? "Auto-computed by formula. Click ƒ to edit the formula." : (canEdit ? "Click to edit" : undefined)}>
          {fmt(display)}
        </span>
        {canEdit && (
          <button className="btn-icon"
            title={hasFormula ? "Edit formula" : "Add formula"}
            onClick={() => openFormulaModal(row.project_id, projectName(row.project_id),
                                            row.particulars, fieldDef.key, fieldDef.label, cell)}
            style={{ padding: 2, color: hasFormula ? "var(--accent)" : "var(--text-muted)", opacity: hasFormula ? 1 : 0.5, fontStyle: "italic", fontWeight: 600 }}>
            ƒ
          </button>
        )}
      </div>
    );
  };

  const sumOfFields = (row, fields) => fields.reduce((acc, f) => {
    const v = cellValue(row.cells[f.key] || {});
    return acc + (v == null ? 0 : Number(v));
  }, 0);
  const sumColumn = (rowsList, fieldKey) => rowsList.reduce((acc, r) => {
    const v = cellValue(r.cells[fieldKey] || {});
    return acc + (v == null ? 0 : Number(v));
  }, 0);

  const renderTable = (title, fields, totalLabel) => (
    <div className="card mb-20">
      <div className="flex justify-between items-center mb-12" style={{ flexWrap: "wrap", gap: 12 }}>
        <h3 className="heading fw-600" style={{ fontSize: 16 }}>{title}</h3>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ minWidth: 60, width: 60, textAlign: "center", verticalAlign: "middle" }}>Sl No</th>
              <th style={{ minWidth: 180, width: 200, textAlign: "center", verticalAlign: "middle" }}>Project</th>
              <th style={{ minWidth: 160, textAlign: "center", verticalAlign: "middle" }}>Particulars</th>
              {fields.map((f) => (
                <th key={f.key} style={{ textAlign: "center", verticalAlign: "middle", minWidth: 130 }}>
                  {f.label}
                </th>
              ))}
              <th style={{ textAlign: "center", verticalAlign: "middle", minWidth: 130 }}>
                <span className="flex items-center justify-center gap-4">
                  {totalLabel}
                  <span title={`Auto-computed: sum of ${fields.length} fields`}
                    style={{ display: "inline-flex", alignItems: "center", color: "var(--accent)" }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
                    </svg>
                  </span>
                </span>
              </th>
              {canEdit && <th style={{ width: 40 }} />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4 + fields.length + (canEdit ? 1 : 0)} className="text-muted"
                      style={{ textAlign: "center", padding: 24 }}>
                {canEdit
                  ? <>No rows yet. Pick a project below and click <strong>+ Add Row</strong>.</>
                  : "No rows yet."}
              </td></tr>
            )}
            {rows.map((row, idx) => {
              const draftKey = rowKey(row.project_id, row.particulars);
              const draft = particularsDraft[draftKey];
              const particularsValue = draft != null ? draft : (row.particulars || "");
              return (
                <tr key={`${row.project_id}::${row.particulars}::${idx}`}>
                  <td className="mono text-xs text-dim" style={{ textAlign: "center" }}>{idx + 1}</td>
                  <td className="fw-500 text-sm">{projectName(row.project_id)}</td>
                  <td>
                    {canEdit ? (
                      <input className="input"
                        style={{ width: "100%", padding: "4px 8px", fontSize: 12 }}
                        value={particularsValue}
                        placeholder="e.g. Phase 1, Building A"
                        onChange={(e) => setParticularsDraft((p) => ({ ...p, [draftKey]: e.target.value }))}
                        onBlur={async () => {
                          if (draft != null && draft !== (row.particulars || "")) {
                            await handleRenameRow(row.project_id, row.particulars, draft);
                          }
                          setParticularsDraft((p) => { const n = { ...p }; delete n[draftKey]; return n; });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); e.target.blur(); }
                          if (e.key === "Escape") {
                            setParticularsDraft((p) => { const n = { ...p }; delete n[draftKey]; return n; });
                            e.target.blur();
                          }
                        }} />
                    ) : (
                      <span className="text-sm">{row.particulars || "—"}</span>
                    )}
                  </td>
                  {fields.map((f) => (
                    <td key={f.key} className="mono text-xs" style={{ textAlign: "right" }}>
                      {renderCell(row, f)}
                    </td>
                  ))}
                  <td className="mono text-xs" style={{ textAlign: "right",
                    color: "var(--text-secondary)", background: "rgba(255,255,255,0.02)" }}>
                    {fmt(sumOfFields(row, fields))}
                  </td>
                  {canEdit && (
                    <td>
                      <button className="btn-icon" title="Delete row"
                        onClick={() => handleDeleteRow(row.project_id, row.particulars)}>
                        <TrashIcon />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {rows.length > 0 && (
              <tr style={{
                background: "rgba(124,143,245,0.14)",
                fontWeight: 700,
                borderTop: "2px solid rgba(124,143,245,0.40)",
              }}>
                <td colSpan={3} className="fw-700" style={{ color: "var(--accent)" }}>Total</td>
                {fields.map((f) => (
                  <td key={f.key} className="mono text-xs fw-700" style={{ textAlign: "right" }}>
                    {fmt(sumColumn(rows, f.key))}
                  </td>
                ))}
                <td className="mono text-xs fw-700" style={{ textAlign: "right" }}>
                  {fmt(rows.reduce((acc, r) => acc + sumOfFields(r, fields), 0))}
                </td>
                {canEdit && <td />}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <div className="info-box mb-20" style={{ padding: "12px 14px" }}>
        <p className="text-xs">
          Multiple particulars per project supported. Click <strong>ƒ</strong> next to any cell to attach a formula that references
          Capex / Means of Finance / project assumptions (e.g. <code className="mono text-accent">{"{Capex - Land}"}</code>, <code className="mono text-accent">{"{Capex Total}"}</code>, <code className="mono text-accent">{"{Finance Cost}"}</code>).
          Per-project sums of every Budget Summary column are exposed back as
          <code className="mono text-accent">{" {Budget - Land}"}</code>, <code className="mono text-accent">{"{Budget - Total Cost}"}</code> (a.k.a. <code className="mono text-accent">{"{Budget - Total with Land Cost}"}</code>), <code className="mono text-accent">{"{Budget - Total MoF}"}</code>, etc., for use in any Variable Template formula.
        </p>
      </div>

      {/* Add Row picker — pick a project then click + Add Row. */}
      {canEdit && projects.length > 0 && (
        <div className="card mb-16" style={{ padding: 14 }}>
          <div className="flex items-center gap-10" style={{ flexWrap: "wrap" }}>
            <span className="text-xs fw-600" style={{ textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-secondary)" }}>
              Add a row
            </span>
            <span className="text-xxs text-dim">Pick a project, then click +.</span>
            <span className="flex gap-6" style={{ marginLeft: "auto", flexWrap: "wrap" }}>
              {projects.map((p) => (
                <button key={p.id} className="btn btn-ghost btn-sm" onClick={() => handleAddRow(p.id)}>
                  <PlusIcon /> {p.name}
                </button>
              ))}
            </span>
          </div>
        </div>
      )}

      {renderTable("Cost", BUDGET_COST_FIELDS, "Total with Land Cost")}
      {renderTable("Means of Finance", BUDGET_MOF_FIELDS, "Total Means of Finance")}

      {formulaModal && (
        <Modal
          title={`Formula — ${formulaModal.projectName}${formulaModal.particulars ? ` · ${formulaModal.particulars}` : ""}: ${formulaModal.label}`}
          width="700px"
          onClose={() => setFormulaModal(null)}>
          <div className="form-group">
            <label className="form-label">Formula Expression <span className="text-dim">(optional)</span></label>
            <input className="input mono"
              value={formulaModal.formula}
              onChange={(e) => setFormulaModal({ ...formulaModal, formula: e.target.value })}
              placeholder="e.g. {Capex - Land}  or  {Capex Total} + {Finance Cost}" />
            <p className="text-xxs text-dim mt-6">
              Evaluated server-side at the project's Actual year. Reference any variable from the Input tab — Capex aggregates,
              project assumptions, synthesized P&amp;L variables. Leave blank to clear and type a value directly instead.
            </p>
          </div>
          <ModalActions
            onCancel={() => setFormulaModal(null)}
            onConfirm={saveFormula}
            confirmLabel={formulaModal.formula ? "Save Formula" : "Clear Formula"} />
        </Modal>
      )}
    </div>
  );
}


/* ─── Multi-select dropdown with search ──────────────────────────
   Lives here (not shared) to avoid pulling InputPage into a refactor
   right now. Mirror of the Report page version. */
function FilterMultiSelect({ placeholder, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = React.useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = new Set(value || []);
  const q = (search || "").trim().toLowerCase();
  const visible = q
    ? options.filter((o) => (o.label || "").toLowerCase().includes(q))
    : options;
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange([...next]);
  };

  const trigger = value.length === 0
    ? placeholder
    : value.length === 1
      ? (options.find((o) => o.id === value[0])?.label || value[0])
      : `${value.length} selected`;

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block", minWidth: 170 }}>
      <button type="button" className="input select"
        style={{ width: "100%", textAlign: "left", paddingRight: 28, cursor: "pointer",
                 color: value.length ? "var(--text-primary)" : "var(--text-muted)" }}
        onClick={() => setOpen((p) => !p)}>
        {trigger}
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0,
          minWidth: 240, zIndex: 50,
          background: "var(--bg-secondary, #15192a)",
          border: "1px solid var(--border)", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)", padding: 8,
        }}>
          <div className="flex items-center gap-6 mb-6">
            <input type="text" autoFocus className="input input-sm"
              placeholder="Search…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, padding: "4px 8px" }} />
            {value.length > 0 && (
              <button type="button" className="btn btn-ghost btn-sm"
                onClick={() => onChange([])}>Clear</button>
            )}
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {visible.length === 0 && (
              <p className="text-xxs text-dim" style={{ padding: "8px 6px" }}>No matches.</p>
            )}
            {visible.map((o) => (
              <label key={o.id} className="flex items-center gap-8"
                style={{
                  cursor: "pointer", padding: "5px 8px", borderRadius: 6,
                  background: selected.has(o.id) ? "rgba(124,143,245,0.10)" : "transparent",
                }}>
                <input type="checkbox" checked={selected.has(o.id)}
                  onChange={() => toggle(o.id)}
                  style={{ accentColor: "var(--accent)" }} />
                <span className="text-xs" style={{
                  flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
